import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { setAccessToken, setPrimaryColor, applyPrimaryColor } from "@/lib/auth";
import { APP_NAME } from "@/lib/brand";
import { loginSchema, type LoginFormData } from "@/validators/auth";
import type { AuthTokens } from "@/types/auth";

const REGISTRATION_OPEN = import.meta.env["VITE_REGISTRATION_OPEN"] !== "false";

export function LoginPage() {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  async function onSubmit(data: LoginFormData) {
    setApiError(null);
    try {
      const res = await api.post<AuthTokens>("/auth/login", data);
      setAccessToken(res.data.accessToken);
      setPrimaryColor(res.data.primaryColor);
      applyPrimaryColor(res.data.primaryColor);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (isAxiosError(err)) {
        const msg = err.response?.data?.error?.message as string | undefined;
        setApiError(msg ?? "Erro ao fazer login. Tente novamente.");
      } else {
        setApiError("Erro inesperado. Tente novamente.");
      }
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {APP_NAME}
          </h1>
          <p className="text-sm text-muted-foreground">Entre na sua conta</p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="seu@email.com"
              aria-invalid={!!errors.email}
              {...register("email")}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              aria-invalid={!!errors.password}
              {...register("password")}
            />
            {errors.password && (
              <p className="text-xs text-destructive">
                {errors.password.message}
              </p>
            )}
          </div>

          {apiError && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {apiError}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Entrando…" : "Entrar"}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            <Link
              to="/forgot-password"
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              Esqueci minha senha
            </Link>
          </p>
        </form>

        {REGISTRATION_OPEN && (
          <p className="text-center text-sm text-muted-foreground">
            Ainda não tem conta?{" "}
            <Link
              to="/register"
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              Cadastre sua transportadora
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
